import React, { useState, useEffect } from 'react';
import { SkeletonLoading } from '@saleshandy/design-system';
import { ArrowsDiagonal } from '@saleshandy/icons';
import { V2Lead } from '../../../types';
import TruncatedTextWithAutoTooltip from '../../../../../shared/components/truncated-text-with-auto-tooltip';
import { Placement } from '../../../../../shared/design-system/components/overlay/enums';
import { accessibleOnClick } from '../../../../../shared/utils';

type LogoCacheEntry =
  | { status: 'loading'; promise: Promise<string> }
  | { status: 'loaded'; objectUrl: string }
  | { status: 'error' };

const companyLogoCache = new Map<string, LogoCacheEntry>();

const fetchCompanyLogoObjectUrl = (url: string): Promise<string> => {
  const existing = companyLogoCache.get(url);

  if (existing) {
    if (existing.status === 'loaded') {
      return Promise.resolve(existing.objectUrl);
    }
    if (existing.status === 'loading') {
      return existing.promise;
    }
  }

  const promise = fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load company logo for ${url}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      companyLogoCache.set(url, { status: 'loaded', objectUrl });
      return objectUrl;
    })
    .catch((error) => {
      companyLogoCache.set(url, { status: 'error' });
      throw error;
    });

  companyLogoCache.set(url, { status: 'loading', promise });
  return promise;
};

interface CompanyCellProps {
  lead: V2Lead;
  onCompanyProfileClick?: (lead: V2Lead, rect: DOMRect) => void;
}

const CompanyLogoFallback: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="20" height="20" rx="2" fill="#D1D5DB" />
    <path
      d="M10.792 2.9996C10.9814 3.02007 11.1635 3.08617 11.3213 3.19296C11.479 3.29969 11.6078 3.4438 11.6973 3.61191C11.7867 3.78017 11.8337 3.96823 11.834 4.15878V16.0074H12.501V6.67343H14.667C14.9763 6.67343 15.2734 6.79661 15.4922 7.01523C15.7109 7.23398 15.8339 7.53106 15.834 7.84042V16.0074H16.667C16.7995 16.0074 16.9268 16.0602 17.0205 16.1539C17.1143 16.2477 17.167 16.3748 17.167 16.5074C17.167 16.64 17.1143 16.7672 17.0205 16.8609C16.9268 16.9546 16.7995 17.0074 16.667 17.0074H3.33398C3.20138 17.0074 3.07424 16.9547 2.98047 16.8609C2.8867 16.7672 2.83398 16.64 2.83398 16.5074C2.83398 16.3748 2.8867 16.2477 2.98047 16.1539C3.07424 16.0601 3.20138 16.0074 3.33398 16.0074H4.16699V6.29257C4.16697 6.05939 4.23745 5.83137 4.36816 5.63828C4.49879 5.44541 4.68415 5.29616 4.90039 5.20956L10.2344 3.07578C10.4112 3.00525 10.6027 2.97917 10.792 2.9996ZM6.66699 14.0074C6.53452 14.0075 6.40715 14.0602 6.31348 14.1539C6.21988 14.2476 6.16699 14.3749 6.16699 14.5074C6.16704 14.6399 6.21982 14.7672 6.31348 14.8609C6.40715 14.9546 6.53452 15.0073 6.66699 15.0074H9.33398C9.46654 15.0074 9.59374 14.9546 9.6875 14.8609C9.78123 14.7672 9.83394 14.64 9.83398 14.5074C9.83398 14.3748 9.78124 14.2477 9.6875 14.1539C9.59373 14.0601 9.46659 14.0074 9.33398 14.0074H6.66699ZM6.66699 11.6734C6.53455 11.6735 6.40715 11.7263 6.31348 11.8199C6.21982 11.9136 6.16712 12.041 6.16699 12.1734C6.16699 12.306 6.21971 12.4342 6.31348 12.5279C6.40715 12.6216 6.53455 12.6733 6.66699 12.6734H9.33398C9.46646 12.6734 9.59376 12.6215 9.6875 12.5279C9.78127 12.4342 9.83398 12.306 9.83398 12.1734C9.83386 12.041 9.78116 11.9136 9.6875 11.8199C9.59376 11.7263 9.46646 11.6734 9.33398 11.6734H6.66699ZM6.66699 9.34042C6.53452 9.34053 6.40715 9.39323 6.31348 9.48691C6.21982 9.58063 6.16703 9.70793 6.16699 9.84042C6.16699 9.9729 6.21988 10.1002 6.31348 10.1939C6.40715 10.2876 6.53452 10.3403 6.66699 10.3404H9.33398C9.46659 10.3404 9.59373 10.2877 9.6875 10.1939C9.78124 10.1002 9.83398 9.97301 9.83398 9.84042C9.83394 9.70787 9.78123 9.58064 9.6875 9.48691C9.59374 9.39321 9.46654 9.34042 9.33398 9.34042H6.66699ZM6.66699 7.00742C6.53452 7.00752 6.40715 7.06022 6.31348 7.1539C6.21988 7.24764 6.16699 7.37494 6.16699 7.50742C6.16704 7.63991 6.21982 7.76721 6.31348 7.86093C6.40715 7.95461 6.53452 8.00731 6.66699 8.00742H9.33398C9.46654 8.00742 9.59374 7.95463 9.6875 7.86093C9.78123 7.7672 9.83394 7.63996 9.83398 7.50742C9.83398 7.37483 9.78124 7.24766 9.6875 7.1539C9.59373 7.06013 9.46659 7.00742 9.33398 7.00742H6.66699Z"
      fill="white"
    />
  </svg>
);

const CompanyCell: React.FC<CompanyCellProps> = ({
  lead,
  onCompanyProfileClick,
}) => {
  const companyName = lead.company_name || '-';
  const companyWebsite = lead.company_website;
  const logoUrl = lead.company_logo_url || null;
  const cachedEntry = logoUrl ? companyLogoCache.get(logoUrl) : undefined;

  const [resolvedLogoSrc, setResolvedLogoSrc] = useState<string | null>(() =>
    cachedEntry?.status === 'loaded' ? cachedEntry.objectUrl : null,
  );
  const [imageError, setImageError] = useState(
    () => cachedEntry?.status === 'error' || false,
  );
  const [imageLoading, setImageLoading] = useState(
    () =>
      Boolean(logoUrl) && (!cachedEntry || cachedEntry.status === 'loading'),
  );

  useEffect(() => {
    let isActive = true;

    if (!logoUrl) {
      setResolvedLogoSrc(null);
      setImageError(false);
      setImageLoading(false);
      return () => {
        isActive = false;
      };
    }

    const entry = companyLogoCache.get(logoUrl);
    if (entry?.status === 'loaded') {
      setResolvedLogoSrc(entry.objectUrl);
      setImageError(false);
      setImageLoading(false);
      return () => {
        isActive = false;
      };
    }

    if (entry?.status === 'error') {
      setResolvedLogoSrc(null);
      setImageError(true);
      setImageLoading(false);
      return () => {
        isActive = false;
      };
    }

    setResolvedLogoSrc(null);
    setImageError(false);
    setImageLoading(true);

    const loadPromise =
      entry?.status === 'loading'
        ? entry.promise
        : fetchCompanyLogoObjectUrl(logoUrl);

    loadPromise
      .then((objectUrl) => {
        if (!isActive) {
          return;
        }
        setResolvedLogoSrc(objectUrl);
        setImageError(false);
        setImageLoading(false);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setResolvedLogoSrc(null);
        setImageError(true);
        setImageLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [logoUrl]);

  const shouldShowFallback = !logoUrl || imageError;
  const shouldShowSkeleton = Boolean(logoUrl) && imageLoading && !imageError;

  // Memoize logo content
  const logoContent = React.useMemo(() => {
    if (shouldShowFallback) {
      return <CompanyLogoFallback />;
    }

    return (
      <>
        {shouldShowSkeleton && (
          <SkeletonLoading className="d-flex" width={24} height={24} />
        )}
        {resolvedLogoSrc && (
          <img
            src={resolvedLogoSrc}
            alt={companyName}
            className="v2-lead-company-logo-img"
            style={{ display: shouldShowSkeleton ? 'none' : 'block' }}
            loading="lazy"
          />
        )}
      </>
    );
  }, [shouldShowFallback, shouldShowSkeleton, companyName, resolvedLogoSrc]);

  const handleNameClick = (e: React.MouseEvent<HTMLElement>) => {
    if (onCompanyProfileClick && lead.company_name) {
      e.preventDefault();
      e.stopPropagation();
      const innerEl = e.currentTarget.querySelector(
        '.v2-lead-name-text--clickable',
      ) as HTMLElement | null;
      const anchorElement = innerEl || e.currentTarget;
      const buttonRect = anchorElement.getBoundingClientRect();
      const indicator = e.currentTarget.querySelector(
        '.v2-lead-modal-open-indicator',
      ) as HTMLElement | null;
      const indicatorRect = indicator?.getBoundingClientRect();
      const anchorRight =
        indicatorRect && indicatorRect.width > 0
          ? indicatorRect.right
          : buttonRect.right;
      const rect = new DOMRect(
        buttonRect.left,
        buttonRect.top,
        anchorRight - buttonRect.left,
        buttonRect.height,
      );
      onCompanyProfileClick(lead, rect);
    }
  };

  if (!lead.company_name?.length) {
    return <div className="v2-lead-company-cell">-</div>;
  }

  let nameContent: React.ReactNode;
  if (lead.company_id) {
    nameContent = (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
      <div className="v2-lead-name-text" onClick={handleNameClick}>
        <div className="v2-lead-name-text--clickable d-flex align-items-center gap-4">
          <TruncatedTextWithAutoTooltip
            text={companyName}
            placement={Placement.Auto}
          />
          <span className="v2-lead-modal-open-indicator" aria-hidden="true">
            <ArrowsDiagonal width={14} height={14} />
          </span>
        </div>
      </div>
    );
  } else if (companyWebsite) {
    nameContent = (
      <div className="v2-lead-name-text">
        <TruncatedTextWithAutoTooltip
          text={companyName}
          className="v2-lead-company-name-link"
          redirectUrl={companyWebsite}
          placement={Placement.Auto}
        />
      </div>
    );
  } else {
    nameContent = (
      <div className="v2-lead-name-text">
        <TruncatedTextWithAutoTooltip
          text={companyName}
          placement={Placement.Auto}
        />
      </div>
    );
  }

  return (
    <div className="v2-lead-company-cell">
      <div
        className="v2-lead-company-logo"
        {...(onCompanyProfileClick && lead.company_name && lead.company_id
          ? accessibleOnClick(handleNameClick)
          : {})}
      >
        {logoContent}
      </div>
      {nameContent}
    </div>
  );
};

// Memoize CompanyCell to prevent unnecessary re-renders
export default React.memo(CompanyCell);
